import { css, LitElement, html } from 'lit';
import { emit } from '@bdc-libs/trinity.dom-helpers';
import { hasChange } from '@bdc-libs/trinity.lit-helpers';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { property } from 'lit/decorators.js';

var Breakpoint;
(function(Breakpoint) {
    Breakpoint["S"] = "s";
    Breakpoint["M"] = "m";
    Breakpoint["L"] = "l";
    Breakpoint["XL"] = "xl";
})(Breakpoint || (Breakpoint = {}));
const breakpoints = {
    s: 576,
    m: 768,
    l: 992,
    xl: 1200
};
const mediaQuery = {
    min: {
        s: css`
      @media only screen and (min-width: ${breakpoints["s"]}px);
    `,
        m: css`
      @media only screen and (min-width: ${breakpoints["m"]}px);
    `,
        l: css`
      @media only screen and (min-width: ${breakpoints["l"]}px);
    `,
        xl: css`
      @media only screen and (min-width: ${breakpoints["xl"]}px);
    `
    },
    max: {
        s: css`
      @media only screen and (max-width: ${breakpoints["s"] - 1}px);
    `,
        m: css`
      @media only screen and (max-width: ${breakpoints["m"] - 1}px);
    `,
        l: css`
      @media only screen and (max-width: ${breakpoints["l"] - 1}px);
    `,
        xl: css`
      @media only screen and (max-width: ${breakpoints["xl"] - 1}px);
    `
    }
};

var THEMES;
(function(THEMES) {
    THEMES["ALTVS"] = "altvs";
    THEMES["BERNARDUS"] = "bernardus";
    THEMES["BDC"] = "bdc";
    THEMES["BILLTEMP"] = "billtemp";
    THEMES["BILL"] = "bill";
    THEMES["BMO"] = "bmo";
    THEMES["BRAND"] = "brand";
    THEMES["CADE"] = "cade";
    THEMES["CAYMUS"] = "caymus";
    THEMES["DIVVY"] = "divvy";
    THEMES["FRB"] = "frb";
    THEMES["FROG"] = "frog";
    THEMES["IBANK"] = "ibank";
    THEMES["KNIGHTS"] = "knightsBridge";
    THEMES["OSS"] = "oss";
    THEMES["ROMBAUER"] = "rombauer";
    THEMES["WHITEROCK"] = "whiteRock";
})(THEMES || (THEMES = {}));
const THEME_CONTAINER_ID_PROP = Symbol('themeContainerId');

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
typeof SuppressedError === "function" ? SuppressedError : function _SuppressedError(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const uuid = ()=>Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
/**
 * Find the nearest theme container in current node's ancestors.
 * @param node Current HTML node
 */ const findThemeContainer = (// eslint-disable-next-line @typescript-eslint/no-explicit-any
node)=>{
    let n = node;
    if (!n || n === window) {
        return undefined;
    }
    if (typeof n[THEME_CONTAINER_ID_PROP] === 'string') {
        return n;
    }
    if (n.assignedSlot) {
        n = n.assignedSlot;
    }
    if (n instanceof ShadowRoot) {
        return findThemeContainer(n.host);
    }
    var _n_parentElement;
    return findThemeContainer((_n_parentElement = n.parentElement) != null ? _n_parentElement : n.parentNode);
};

/**
 * @todo - Consolidate token files (i.e single token file that can be used for local + global props)
 * @todo - Get component tokens in object format and load conditionally like base theme files.
 */ // @customElement('tri-theme-container')
class TriThemeContainer extends ScopedElementsMixin(LitElement) {
    static get tagName() {
        return 'tri-theme-container';
    }
    static get eventBindings() {
        return {
            onThemeChange: 'theme-change'
        };
    }
    connectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.connectedCallback();
        // eslint-disable-next-line wc/no-self-class
        this.classList.add(TriThemeContainer.CLASSNAME);
    }
    async updated(changedProps) {
        super.updated(changedProps);
        if (hasChange(this, changedProps, 'theme')) {
            emit(this, 'theme-change', {
                detail: {
                    theme: this.theme
                }
            });
            switch(this.theme){
                case THEMES.ALTVS:
                    this.__setCSSVars((await import('@bill/pathfinder.altvs')).cssTokens);
                    break;
                case THEMES.BERNARDUS:
                    this.__setCSSVars((await import('@bill/pathfinder.bernardus')).cssTokens);
                    break;
                case THEMES.BDC:
                    this.__setCSSVars((await import('@bill/pathfinder.bdc')).cssTokens);
                    break;
                case THEMES.BILL:
                    this.__setCSSVars((await import('@bill/pathfinder.bill')).cssTokens);
                    break;
                case THEMES.BILLTEMP:
                    this.__setCSSVars((await import('@bill/pathfinder.bill-temp')).cssTokens);
                    break;
                case THEMES.BMO:
                    this.__setCSSVars((await import('@bill/pathfinder.bmo')).cssTokens);
                    break;
                case THEMES.BRAND:
                    this.__setCSSVars((await import('@bill/pathfinder.brand')).cssTokens);
                    break;
                case THEMES.CADE:
                    this.__setCSSVars((await import('@bill/pathfinder.cade')).cssTokens);
                    break;
                case THEMES.CAYMUS:
                    this.__setCSSVars((await import('@bill/pathfinder.caymus')).cssTokens);
                    break;
                case THEMES.DIVVY:
                    this.__setCSSVars((await import('@bill/pathfinder.divvy')).cssTokens);
                    break;
                case THEMES.FRB:
                    this.__setCSSVars((await import('@bill/pathfinder.frb')).cssTokens);
                    break;
                case THEMES.FROG:
                    this.__setCSSVars((await import('@bill/pathfinder.frog')).cssTokens);
                    break;
                case THEMES.IBANK:
                    this.__setCSSVars((await import('@bill/pathfinder.ibank')).cssTokens);
                    break;
                case THEMES.KNIGHTS:
                    this.__setCSSVars((await import('@bill/pathfinder.knights-bridge')).cssTokens);
                    break;
                case THEMES.OSS:
                    // this.__setCSSVars(themeObjs.ossTriTheme.properties);
                    this.__setCSSVars((await import('@bill/pathfinder.up')).cssTokens);
                    break;
                case THEMES.ROMBAUER:
                    this.__setCSSVars((await import('@bill/pathfinder.rombauer')).cssTokens);
                    break;
                case THEMES.WHITEROCK:
                    this.__setCSSVars((await import('@bill/pathfinder.white-rock')).cssTokens);
                    break;
            }
        }
    }
    /** If the top level theme container, add css properties to document root. Otherwise, set on theme container component. */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __setCSSVars(properties) {
        if (!properties) {
            return;
        }
        if (!findThemeContainer(this.parentNode)) {
            this.__setGlobalCSSVars(properties);
        } else {
            this.__setLocalCSSVars(properties);
        }
    }
    /** Adds CSS custom properties to document root */ // eslint-disable-next-line class-methods-use-this
    __setGlobalCSSVars(properties) {
        Object.keys(properties).forEach((key)=>{
            document.documentElement.style.setProperty(key, properties[key]);
        });
    }
    /** Adds CSS custom properties to component host */ __setLocalCSSVars(properties) {
        Object.keys(properties).forEach((key)=>{
            this.style.setProperty(key, properties[key]);
        });
    }
    render() {
        return html`<slot></slot>`;
    }
    constructor(...args){
        super(...args);
        /** The current active theme */ this.theme = THEMES.BDC;
        this[THEME_CONTAINER_ID_PROP] = uuid();
    }
}
TriThemeContainer.CLASSNAME = 'TriThemeContainer';
__decorate([
    property({
        type: String,
        reflect: true
    })
], TriThemeContainer.prototype, "theme", void 0);

class ThemeAwareController {
    get themeId() {
        return this.__themeId;
    }
    __handleThemeChange() {
        var _this___themeContainer;
        const newThemeId = ((_this___themeContainer = this.__themeContainer) == null ? void 0 : _this___themeContainer.theme) || THEMES.BDC;
        if (this.__themeId !== newThemeId) {
            this.__themeId = newThemeId;
            this.__host.requestUpdate();
            this.__onThemeChange();
        }
    }
    hostConnected() {
        setTimeout(()=>{
            var _this___themeContainer;
            this.__themeContainer = findThemeContainer(this.__host);
            if (!this.__themeContainer) {
                var _document_querySelector;
                // If we cannot find a theme container, query for the 1st-found from document
                this.__themeContainer = (_document_querySelector = document.querySelector(`.${TriThemeContainer.CLASSNAME}`)) != null ? _document_querySelector : undefined;
            }
            (_this___themeContainer = this.__themeContainer) == null ? void 0 : _this___themeContainer.addEventListener('theme-change', this.__handleThemeChange);
            this.__handleThemeChange();
        });
    }
    hostDisconnected() {
        if (this.__themeContainer) {
            this.__themeContainer.removeEventListener('theme-change', this.__handleThemeChange);
        }
    }
    constructor(host, onThemeChange = ()=>{}){
        this.__host = host;
        host.addController(this);
        this.__onThemeChange = onThemeChange;
        this.__handleThemeChange = this.__handleThemeChange.bind(this);
    }
}

export { Breakpoint, THEMES, ThemeAwareController, TriThemeContainer, breakpoints, findThemeContainer, mediaQuery };
